import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { Context } from 'bernie-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { HotelMapModel, MapMarker } from 'models/hotel/map-types';
import { MarkerIcon } from 'src/common/models/hotel/map-types/map-marker';

import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkMap } from 'uitk-react-map';

import { MapMarkers } from 'components/infosite/property-details/property-map/map-markers';
import { MapPills } from './map-pills';
import { MapSheet } from 'components/infosite/property-details/property-map/map-sheet';

export interface MapDialogProps {
  analytics?: AnalyticsStore;
  context?: Context;
  currentHotel?: CurrentHotelStore;
  mapModel: HotelMapModel;
  onDismiss: () => void;
  onMapClick?: () => void;
  onSelect?: (pin) => void;
  trackMarkerSelect?: (icon) => void;
}

interface MapDialogState {
  selectedMarker: any;
  showSheet?: boolean;
  pinFilters?: Set<MarkerIcon>;
}

interface TrackSelectMarker {
  HOTEL: () => void;
  LANDMARK: () => void;
  AIRPORT: () => void;
  TRAIN: () => void;
  METRO: () => void;
  RESTAURANT: () => void;
  CIRCLE: () => void;
}

@inject('context', 'analytics', 'currentHotel')
export class MapDialog extends React.Component<MapDialogProps, MapDialogState> {
  private pageName: string;
  private zIndex: number;
  private trackMapPan: () => void;
  private trackMapZoomIn: () => void;
  private trackMapZoomOut: () => void;
  private trackSelectMarker: TrackSelectMarker;
  private trackLandmarksFilterApplied: () => void;
  private trackRestaurantsFilterApplied: () => void;
  private trackNoFiltersApplied: () => void;
  private trackBothFiltersApplied: () => void;

  constructor(props: MapDialogProps) {
    super(props);

    const { analytics } = this.props;
    this.setUpAnalyticsTrackers(analytics);
    // binding these for efficiency
    this.getMarkers = this.getMarkers.bind(this);
    this.onMarkerSelect = this.onMarkerSelect.bind(this);
    this.validateMarkers = this.validateMarkers.bind(this);
    this.dismissSheet = this.dismissSheet.bind(this);
    this.trackMarkerSelect = this.trackMarkerSelect.bind(this);
    this.createTrackSelectMarker = this.createTrackSelectMarker.bind(this);
    this.onPillToggle = this.onPillToggle.bind(this);

    // set all color values once in constructor, fallback values for older browsers without css vars
    this.pageName = 'HotelInformation';
    this.zIndex = (props.mapModel.markers && props.mapModel.markers.length) || 0;
    this.state = {
      showSheet: false,
      selectedMarker: null,
      pinFilters: props.mapModel.filters && new Set(props.mapModel.filters.map(filter => MarkerIcon[filter.type.toString()]))
    };
  }

  public componentWillMount() {
    this.props.currentHotel.fetchPoiContent(this.props.context);
  }

  private getMarkers(markers?: MapMarker[]) {
    const { pinFilters } = this.state;

    return pinFilters && Boolean(markers.length)
      ? markers.filter(marker => {
          const icon = MarkerIcon[marker.icon.toString()];
          const defaultCases = [MarkerIcon.HOTEL, MarkerIcon.AIRPORT, MarkerIcon.CIRCLE];

          return pinFilters.has(icon) || defaultCases.includes(icon);
        })
      : markers;
  }

  private setUpAnalyticsTrackers(analytics) {
    const noop = () => null;
    const clickTracker = analytics && analytics.clickTracker ? analytics.clickTracker.bind(analytics) : noop;
    this.trackMapPan = clickTracker('Module.Map.Pan', 'User panning PWA HIS Map', null, noop);
    this.trackMapZoomIn = clickTracker('Module.Map.Zoom.In', 'User zooming in on PWA HIS Map', null, noop);
    this.trackMapZoomOut = clickTracker('Module.Map.Zoom.Out', 'User zooming out on PWA HIS Map', null, noop);
    this.trackSelectMarker = this.createTrackSelectMarker();
    this.trackLandmarksFilterApplied = clickTracker('Module.Map.LandmarksFilterApplied', 'Show landmarks on HIS map', null, noop);
    this.trackRestaurantsFilterApplied = clickTracker('Module.Map.RestaurantsFilterApplied', 'Show restaurants on HIS map', null, noop);
    this.trackNoFiltersApplied = clickTracker('Module.Map.NoFiltersApplied', 'Show only airports and hotel on HIS map', null, noop);
    this.trackBothFiltersApplied = clickTracker('Module.Map.BothFiltersApplied', 'Show landmarks and restaurants on HIS map', null, noop);
  }

  private createTrackSelectMarker(): TrackSelectMarker {
    const { analytics } = this.props;
    const noop = () => null;
    const clickTracker = analytics && analytics.clickTracker ? analytics.clickTracker.bind(analytics) : noop;

    return {
      HOTEL: clickTracker('Module.Map.HotelMarkerSelect', 'A hotel marker selected on HIS map', null, noop),
      LANDMARK: clickTracker('Module.Map.PlaceMarkerSelect', 'A landmark marker selected on HIS map', null, noop),
      AIRPORT: clickTracker('Module.Map.AirportMarkerSelect', 'An Airport marker selected on HIS map', null, noop),
      TRAIN: clickTracker('Module.Map.TransitMarkerSelect', 'A train marker selected on HIS map', null, noop),
      METRO: clickTracker('Module.Map.TransitMarkerSelect', 'A train marker selected on HIS map', null, noop),
      RESTAURANT: clickTracker('Module.Map.RestaurantMarkerSelect', 'A restaurant marker selected on HIS map', null, noop),
      CIRCLE: clickTracker('Module.Map.CircleMarkerSelect', 'A circle marker selected on HIS map', null, noop)
    };
  }

  public onPillToggle(type: MarkerIcon) {
    const { pinFilters } = this.state;
    const newPinFilter = new Set(pinFilters);

    if (newPinFilter.has(type)) {
      newPinFilter.delete(type);
    } else {
      newPinFilter.add(type);
    }

    this.trackMapPinFilters(newPinFilter);

    this.setState({
      pinFilters: newPinFilter
    });
  }

  public trackMapPinFilters(pinFilters: Set<MarkerIcon>) {
    if (pinFilters.has(MarkerIcon.LANDMARK) && !pinFilters.has(MarkerIcon.RESTAURANT)) {
      this.trackLandmarksFilterApplied();
    } else if (!pinFilters.has(MarkerIcon.LANDMARK) && pinFilters.has(MarkerIcon.RESTAURANT)) {
      this.trackRestaurantsFilterApplied();
    } else if (!pinFilters.has(MarkerIcon.LANDMARK) && !pinFilters.has(MarkerIcon.RESTAURANT)) {
      this.trackNoFiltersApplied();
    } else {
      this.trackBothFiltersApplied();
    }
  }

  public render() {
    const { mapModel, currentHotel } = this.props;
    const { selectedMarker, showSheet } = this.state;
    const toolBarAttributes = {
      type: ToolbarType.CLOSE,
      header: mapModel.title,
      subtext: currentHotel && currentHotel.propertyLocationScoreInfo && currentHotel.propertyLocationScoreInfo.score
    };

    const toolbarAnalytics = this.props.analytics.setupTracker('.Module.Map.Link', 'Close map dialog');

    const dialogProps = {
      dialogShow: true,
      strictFullscreen: true
    };

    const mapProps = {
      bounds: mapModel.bounds,
      center: mapModel.center,
      countryCode: this.props.context.site.country,
      locale: this.props.context.locale,
      pageName: this.pageName,
      onPan: this.trackMapPan,
      onZoomIn: this.trackMapZoomIn,
      onZoomOut: this.trackMapZoomOut,
      zoom: mapModel.zoom
    };

    const mapMarkersProps = {
      analytics: this.props.analytics,
      markers: this.getMarkers(this.validateMarkers(mapModel.markers)),
      onSelect: this.onMarkerSelect,
      onMapClick: this.dismissSheet,
      trackMarkerSelect: this.trackMarkerSelect
    };

    const mapPillsProps = {
      activePills: this.state.pinFilters,
      filters: mapModel.filters,
      onPillToggle: this.onPillToggle
    };

    return (
      <UitkFullscreenDialog {...dialogProps}>
        <UitkToolbar {...toolBarAttributes} action={this.props.onDismiss} analytics={toolbarAnalytics} />
        {mapModel.filters && <MapPills {...mapPillsProps} />}
        <section className="uitk-dialog-content default-padding">
          <UitkMap {...mapProps} hideGooglePOIs={true}>
            <MapMarkers {...mapMarkersProps} />
          </UitkMap>
          {showSheet &&
            selectedMarker.sheet &&
            selectedMarker.sheet.title && (
              <MapSheet
                isVisible={showSheet}
                title={selectedMarker.sheet.title}
                subtitle={selectedMarker.sheet.subtitle}
                description={selectedMarker.sheet.description || selectedMarker.sheet.type}
                photoUrl={selectedMarker.sheet.photoUrl}
                photoAlt={selectedMarker.sheet.photoAlt}
              />
            )}
        </section>
      </UitkFullscreenDialog>
    );
  }

  private onMarkerSelect(marker) {
    const { selectedMarker } = this.state;
    if (selectedMarker && typeof selectedMarker.blur === 'function') {
      selectedMarker.blur();
    }

    this.zIndex += 1;

    if (marker && typeof marker.focus === 'function') {
      marker.focus(this.zIndex);
    }
    if (marker.sheet && this.props.currentHotel.poiData[marker.sheet.id]) {
      const markerId = marker.sheet.id;
      marker.sheet.photoUrl = this.props.currentHotel.poiData[markerId].photoUrl;
      marker.sheet.description = this.props.currentHotel.poiData[markerId].description;
    }
    this.setState({
      selectedMarker: marker,
      showSheet: true
    });
  }

  private validateMarkers(markers) {
    return markers && markers.filter(marker => marker.position != null);
  }

  private dismissSheet(expectedMarker?: MapMarker) {
    const { selectedMarker } = this.state;

    if (
      selectedMarker &&
      selectedMarker.sheet &&
      expectedMarker &&
      expectedMarker.sheet &&
      expectedMarker.sheet.id !== selectedMarker.sheet.id
    ) {
      return null;
    }

    if (selectedMarker && typeof selectedMarker.blur === 'function') {
      selectedMarker.blur();
    }

    this.setState({
      showSheet: false
    });
  }

  private trackMarkerSelect(icon) {
    if (this.trackSelectMarker[icon]) {
      this.trackSelectMarker[icon]();
    }
  }
}
